import { PanelMenu } from 'primereact/panelmenu';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getActiveKeys } from './utils';
import { Image } from 'primereact/image';
import Logo from '@assets/images/logo.svg';
import Favicon from '@assets/images/favicon.svg';
import { Tooltip } from 'primereact/tooltip';
import { Link } from 'react-router-dom';

const CollapsedMenuItem = ({ item }: { item: any }) => {
	const [showFlyout, setShowFlyout] = useState(false);
	const iconRef = useRef<HTMLDivElement>(null);
	const flyoutRef = useRef<HTMLDivElement>(null);
	const hasChildren = item.items && item.items.length > 0;
	const isActive = item.activeKeys?.includes(item.route);
	const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

	const showMenu = useCallback(() => {
		clearTimeout(hideTimeout.current);
		setShowFlyout(true);
	}, []);

	const hideMenu = useCallback(() => {
		hideTimeout.current = setTimeout(() => setShowFlyout(false), 100);
	}, []);

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (hasChildren) {
			const firstChild = item.items[0];
			if (firstChild?.command) {
				firstChild.command();
			}
		} else if (item.command) {
			item.command();
		}
	};

	const getFlyoutPosition = () => {
		if (!iconRef.current) return { top: 0, left: 0 };
		const rect = iconRef.current.getBoundingClientRect();
		return { top: rect.top, left: rect.right + 8 };
	};

	return (
		<div
			ref={iconRef}
			onMouseEnter={() => hasChildren && showMenu()}
			onMouseLeave={() => hasChildren && hideMenu()}
		>
			<a
				href={hasChildren ? undefined : item.route}
				onClick={handleClick}
				className={`collapsed-menu-item-${item.key} flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 cursor-pointer ${
					isActive
						? 'bg-[var(--color-sidebar-active-bg)] text-[var(--color-sidebar-active-text)]'
						: 'text-[var(--color-sidebar-text-muted)] hover:bg-[var(--color-sidebar-hover-bg)] hover:text-[var(--color-sidebar-text)]'
				}`}
			>
				<span className="text-base leading-none">{item.icon}</span>
			</a>
			{!hasChildren && (
				<Tooltip target={`.collapsed-menu-item-${item.key}`} content={item.label} position="right" />
			)}
			{hasChildren &&
				showFlyout &&
				createPortal(
					<div
						ref={flyoutRef}
						onMouseEnter={showMenu}
						onMouseLeave={hideMenu}
						className="fixed z-[1100] min-w-[180px] py-1.5 bg-skin-card border border-skin rounded-xl shadow-overlay"
						style={getFlyoutPosition()}
					>
						<div className="px-3 py-2 text-[11px] font-semibold text-skin-faint uppercase tracking-wider">
							{item.label}
						</div>
						{item.items.map((child: Record<string, any>) => {
							const isChildActive = child.activeKeys?.includes(child.route);
							return (
								<a
									key={child.key}
									href={child.route}
									onClick={(e: React.MouseEvent) => {
										if (child.command) {
											e.preventDefault();
											child.command();
										}
										setShowFlyout(false);
									}}
									className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-all ${
										isChildActive
											? 'bg-[var(--color-sidebar-active-bg)] text-[var(--color-sidebar-active-text)] font-medium'
											: 'text-[var(--color-sidebar-text)] hover:bg-[var(--color-sidebar-hover-bg)]'
									}`}
								>
									<span className="text-base">{child.icon}</span>
									<span>{child.label}</span>
								</a>
							);
						})}
					</div>,
					document.body
				)}
		</div>
	);
};

const STORAGE_KEY = 'sidebar-expanded-keys';

const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
	useEffect(() => {
		const handler = () => setIsMobile(window.innerWidth < 640);
		window.addEventListener('resize', handler);
		return () => window.removeEventListener('resize', handler);
	}, []);
	return isMobile;
};

const NavSidebar = ({ menuList, onClose, collapsed = false, onToggleCollapse }: Record<string, any>) => {
	const isMobile = useIsMobile();
	const isCollapsed = isMobile ? false : collapsed;
	const [expandedKeys, setExpandedKeys] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			return saved ? JSON.parse(saved) : {};
		} catch {
			return {};
		}
	});
	const [initialized, setInitialized] = useState(false);

	// On first load, merge active route keys with saved keys
	useEffect(() => {
		if (!initialized && menuList.length > 0) {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				setExpandedKeys(JSON.parse(saved));
			} else {
				const activeKeys = getActiveKeys(menuList);
				setExpandedKeys(activeKeys);
			}
			setInitialized(true);
		}
	}, [menuList, initialized]);

	// Persist to localStorage whenever expandedKeys changes
	useEffect(() => {
		if (initialized) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedKeys));
		}
	}, [expandedKeys, initialized]);

	const parentKeys = menuList
		.filter((item: Record<string, any>) => item.items && item.items.length > 0)
		.map((item: Record<string, any>) => item.key);

	const allExpanded = parentKeys.length > 0 && parentKeys.every((key: string) => expandedKeys[key]);

	const toggleAll = () => {
		if (allExpanded) {
			setExpandedKeys({});
		} else {
			const allKeys: Record<string, boolean> = {};
			parentKeys.forEach((key: string) => {
				allKeys[key] = true;
			});
			setExpandedKeys(allKeys);
		}
	};

	return (
		<>
			{/* Mobile backdrop — tap to close */}
			<div
				className="fixed inset-0 z-[998] bg-[var(--color-backdrop)] backdrop-blur-sm sm:hidden"
				onClick={onClose}
			/>
			<div
				className={`h-screen z-[999] fixed sm:sticky shrink-0 bg-[var(--color-sidebar-bg)] border-r border-[var(--color-border-muted)] transition-all duration-300 flex flex-col ${
					isCollapsed ? 'sm:w-[60px] w-[260px]' : 'w-[256px]'
				}`}
			>
				{/* Header / Logo Section */}
				<div
					className={`flex items-center ${
						isCollapsed ? 'flex-col gap-1 px-2 py-2' : 'justify-between px-2 py-2'
					}`}
				>
					{/* Brand tile */}
					<div className={`flex items-center gap-2 p-2 rounded-md min-w-0 ${isCollapsed ? '' : 'flex-1'}`}>
						<Link to="/dashboard" className="shrink-0 cursor-pointer block">
							{isCollapsed ? (
								<img
									className="w-8 h-8 rounded-lg dark:invert dark:brightness-[0.85]"
									src={Favicon}
									alt="HK"
								/>
							) : (
								<img className="w-8 h-8 rounded-lg dark:invert dark:brightness-[0.85]" src={Favicon} alt="HK" />
							)}
						</Link>
						{!isCollapsed && (
							<Link
								to="/dashboard"
								className="text-sm font-medium text-skin-base truncate leading-tight hover:text-skin-muted transition-colors"
							>
								HouseKraft ERP
							</Link>
						)}
					</div>

					{/* Collapse toggle — desktop only */}
					{!isCollapsed && (
						<button
							className="hidden sm:flex items-center justify-center w-[26px] h-[26px] rounded-md bg-skin-card border border-skin text-[var(--color-sidebar-text-muted)] hover:text-[var(--color-sidebar-text)] transition-all duration-200 cursor-pointer shrink-0 mr-1"
							onClick={onToggleCollapse}
							aria-label="Collapse sidebar"
						>
							<span className="pi pi-angle-left text-xs" />
						</button>
					)}
					{isCollapsed && (
						<button
							className="hidden sm:flex items-center justify-center w-[26px] h-[26px] rounded-md bg-skin-card border border-skin text-[var(--color-sidebar-text-muted)] hover:text-[var(--color-sidebar-text)] transition-all duration-200 cursor-pointer"
							onClick={onToggleCollapse}
							aria-label="Expand sidebar"
						>
							<span className="pi pi-angle-right text-xs" />
						</button>
					)}

					{/* Mobile close button */}
					<button
						className="flex items-center justify-center w-7 h-7 rounded-md bg-skin-card border border-skin text-[var(--color-sidebar-text-muted)] hover:text-[var(--color-sidebar-text)] transition-colors sm:hidden cursor-pointer"
						onClick={onClose}
						aria-label="Close sidebar"
					>
						<span className="pi pi-times text-xs" />
					</button>
				</div>

				{/* Menu Section */}
				<div className={`flex flex-col flex-1 overflow-y-auto scrollbar-thin ${isCollapsed ? 'px-1.5 pt-2' : 'px-2 pt-1'}`}>
					{/* Expand/Collapse All toggle */}
					{!isCollapsed && parentKeys.length > 0 && (
						<button
							onClick={toggleAll}
							className="flex items-center gap-1.5 px-2 py-1.5 mb-1 text-[11px] font-medium text-[var(--color-sidebar-text-muted)] hover:text-[var(--color-sidebar-text)] transition-colors rounded-lg hover:bg-[var(--color-sidebar-hover-bg)] self-end border-none bg-transparent cursor-pointer"
						>
							<span
								className={`pi ${allExpanded ? 'pi-angle-double-up' : 'pi-angle-double-down'} text-xs`}
							/>
							{allExpanded ? 'Collapse' : 'Expand'}
						</button>
					)}
					<div className="grow">
						{isCollapsed ? (
							<div className="flex flex-col items-center gap-1 pt-1">
								{menuList.map((item: Record<string, any>) => (
									<CollapsedMenuItem key={item.key} item={item} />
								))}
							</div>
						) : (
							<PanelMenu
								model={menuList}
								expandedKeys={expandedKeys}
								onExpandedKeysChange={setExpandedKeys}
								className="w-full"
								pt={{
									root: { className: 'flex flex-col gap-0.5' },
									header: { className: 'cursor-pointer' },
									headerContent: { className: 'bg-[var(--color-sidebar-bg)] border-0 rounded-lg' },
									content: { className: 'bg-[var(--color-sidebar-bg)]' },
									menu: { className: 'flex flex-col ml-6 border-[var(--color-border-muted)]' },
									menuitem: { className: 'cursor-pointer border-l-[2px]' },
									menuContent: { className: 'border-0 pl-3 rounded-md' }
								}}
							/>
						)}
					</div>
				</div>

				{/* Section divider before footer */}
				{!isCollapsed && (
					<div className="border-t border-skin-muted mt-3 pt-3 px-2 pb-2">
						<div className="flex items-center justify-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-[var(--color-success)] animate-pulse" />
							<span className="text-[11px] font-medium text-[var(--color-sidebar-text-muted)]">
								HouseKraft ERP
							</span>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default NavSidebar;
